import { NgModule } from '@angular/core'
import { ChatUiModule } from './chat-ui.module'
import { ChatAudioPlayerComponent } from './chat-audio-player/chat-audio-player.component'
import { ChatComposerComponent } from './chat-composer/chat-composer.component'
import { ChatContextRefCardComponent } from './chat-context-ref/chat-context-ref-card.component'
import { ChatContextRefPickerDialogComponent } from './chat-context-ref/chat-context-ref-picker-dialog.component'
import { ChatConversationComponent } from './chat-conversation/chat-conversation.component'
import { ChatConversationListComponent } from './chat-conversation-list/chat-conversation-list.component'
import { ChatConnectionBannerComponent } from './chat-states/chat-connection-banner.component'
import { ChatEmptyStateComponent } from './chat-states/chat-empty-state.component'
import { ChatErrorStateComponent } from './chat-states/chat-error-state.component'
import { ChatMessageListSkeletonComponent } from './chat-states/chat-message-list-skeleton.component'
import { ChatSidebarSkeletonComponent } from './chat-states/chat-sidebar-skeleton.component'
import { ChatUnavailableComponent } from './chat-states/chat-unavailable.component'
import { ChatSwitcherComponent } from './chat-switcher/chat-switcher.component'
import { ChatThreadComponent } from './chat-thread/chat-thread.component'
import { ChatChannelBrowseDialogComponent } from './dialogs/chat-channel-browse-dialog/chat-channel-browse-dialog.component'
import { ChatChannelCreateDialogComponent } from './dialogs/chat-channel-create-dialog/chat-channel-create-dialog.component'
import { ChatChannelInviteDialogComponent } from './dialogs/chat-channel-invite-dialog/chat-channel-invite-dialog.component'
import { ChatChannelMembersDialogComponent } from './dialogs/chat-channel-members-dialog/chat-channel-members-dialog.component'
import { ChatConfirmDialogComponent } from './dialogs/chat-confirm-dialog/chat-confirm-dialog.component'
import { ChatDirectCreateDialogComponent } from './dialogs/chat-direct-create-dialog/chat-direct-create-dialog.component'
import { ChatFavoriteSectionDialogComponent } from './dialogs/chat-favorite-section-dialog/chat-favorite-section-dialog.component'
import { ChatForwardDialogComponent } from './dialogs/chat-forward-dialog/chat-forward-dialog.component'
import { ChatImageViewerDialogComponent } from './dialogs/chat-image-viewer-dialog/chat-image-viewer-dialog.component'
import { ChatMarkdownPipe } from './pipes/chat-markdown.pipe'
import { ChatSavedComponent } from './panes/chat-saved/chat-saved.component'
import { ChatSearchComponent } from './panes/chat-search/chat-search.component'
import { ChatThreadsComponent } from './panes/chat-threads/chat-threads.component'

/**
 * Internal Chat shared UI (SPEC-2026-07-internal-chat, T-18/T-19/T-20/T-34) —
 * the components used by BOTH the eager bubble popup (single-pane conversation +
 * quick reply) and the lazy `/chat` full view: one conversation pane, one
 * composer, one thread panel, one forward dialog, one audio player, one markdown
 * render pipe — no duplication.
 *
 * The thread panel, the forward dialog, the T-35 invite/favorite-section dialogs
 * and the two CREATE dialogs (channel/group and direct) live here (not in
 * ChatModule) because the conversation pane and the bubble popup open them, so
 * both surfaces need them — the sidebar reaches the same declarations through this
 * module. They moved here as the popup grew the matching entry points: left in the
 * LAZY ChatModule they compiled fine but would only work after `/chat` had been
 * visited once, and the popup must not depend on a route that may be retired. The
 * dialogs themselves only import from `state/`, so nothing here reaches into
 * `chat-page/` or `chat-sidebar/`. Same reason for
 * the T-22 pieces: the work-reference card renders inside a message, the
 * reference picker is opened from the composer, and the quick switcher (Ctrl/
 * Cmd+K) must exist on every authenticated route — not only under `/chat`.
 *
 * T-25 adds the interface-state pieces (`chat-states/`): the connection banner
 * plus the reusable skeleton / empty / error-with-retry / chat-unavailable
 * components. They live here for the same reason — every chat surface (popup,
 * `/chat` workspace, sidebar, conversation pane, saved and search views) must
 * be able to render them from one implementation.
 *
 * SPEC-2026-09-internal-chat-pwa (T-24) adds `<chat-conversation-list>`: the list
 * the product's bubble and the chat app's rail both render. The saved, search and
 * threads panes (`panes/`) moved in with it, so the chat app renders them too.
 *
 * Declaration-only module — no providers, so being imported by an eager AND a
 * lazy module is harmless.
 */
@NgModule({
  imports: [ChatUiModule],
  declarations: [
    ChatConfirmDialogComponent,
    ChatConversationComponent,
    ChatConversationListComponent,
    ChatComposerComponent,
    ChatThreadComponent,
    ChatForwardDialogComponent,
    ChatImageViewerDialogComponent,
    ChatChannelInviteDialogComponent,
    ChatChannelMembersDialogComponent,
    ChatFavoriteSectionDialogComponent,
    ChatChannelCreateDialogComponent,
    ChatDirectCreateDialogComponent,
    ChatChannelBrowseDialogComponent,
    ChatAudioPlayerComponent,
    ChatContextRefCardComponent,
    ChatContextRefPickerDialogComponent,
    ChatSwitcherComponent,
    ChatConnectionBannerComponent,
    ChatSidebarSkeletonComponent,
    ChatMessageListSkeletonComponent,
    ChatEmptyStateComponent,
    ChatErrorStateComponent,
    ChatUnavailableComponent,
    ChatMarkdownPipe,
    ChatSavedComponent,
    ChatSearchComponent,
    ChatThreadsComponent,
  ],
  exports: [
    ChatConversationComponent,
    ChatConversationListComponent,
    ChatComposerComponent,
    ChatThreadComponent,
    ChatAudioPlayerComponent,
    ChatContextRefCardComponent,
    ChatConnectionBannerComponent,
    ChatSidebarSkeletonComponent,
    ChatMessageListSkeletonComponent,
    ChatEmptyStateComponent,
    ChatErrorStateComponent,
    ChatUnavailableComponent,
    ChatMarkdownPipe,
    ChatSavedComponent,
    ChatSearchComponent,
    ChatThreadsComponent,
  ],
})
export class ChatSharedModule {}
